import * as React from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
  Alert,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate } from "react-router";
import PageContainer from "./PageContainer";
import { uploadResume, getAllResumes } from "../../services/resumeService";
import useNotifications from "../hooks/useNotifications/useNotifications";

export default function ResumeUploadPage() {
  const navigate = useNavigate();
  const notifications = useNotifications();

  const [file, setFile] = React.useState(null);
  const [resumeExists, setResumeExists] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    const fetchResume = async () => {
      try {
        const res = await getAllResumes();
        if (res?.length > 0) setResumeExists(true);
      } catch (err) {
        setError(err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchResume();
  }, []);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) setFile(selectedFile);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const droppedFile = event.dataTransfer.files[0];
    if (droppedFile) setFile(droppedFile);
  };

  const handleRemove = () => setFile(null);

  const handleBack = () => navigate("/app/user/resume");

  const handleUpload = async () => {
    if (!file) return;
    setIsLoading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await uploadResume(formData);

      if (response.success) {
        notifications.show(resumeExists ? "Resume updated" : "Resume uploaded", {
          severity: "success",
          autoHideDuration: 3000
        });
        navigate("/app/user/resume");
      } else {
        notifications.show(response.message || "Upload failed", {
          severity: "error",
          autoHideDuration: 3000
        });
        navigate("/app/user/resume");
      }
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <PageContainer title={resumeExists ? "Update Resume" : "Upload Resume"}>
        <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
          <CircularProgress />
        </Box>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer title={resumeExists ? "Update Resume" : "Upload Resume"}>
        <Alert severity="error">{error.message}</Alert>
      </PageContainer>
    );
  }

  return (
    <PageContainer
      title={resumeExists ? "Update Resume" : "Upload Resume"}
      breadcrumbs={[
        { title: "Resumes", path: "/app/user/resume" },
        { title: resumeExists ? "Update Resume" : "Upload Resume" },
      ]}
    >
      <Stack spacing={3} sx={{ width: "100%" }}>
        {/* Instructions */}
        <Paper sx={{ p: 3 }} elevation={2}>
          <Typography variant="overline">Instructions</Typography>
          <Typography variant="body1">
            {resumeExists
              ? "You can update your existing resume (PDF, DOC, or DOCX)."
              : "Upload your resume (PDF, DOC, or DOCX). This will be analyzed and matched with job opportunities."}
          </Typography>
        </Paper>

        {/* Upload */}
        <Paper
          sx={{
            p: 3,
            border: "2px dashed",
            borderColor: "divider",
            textAlign: "center",
            borderRadius: 3,
            bgcolor: "background.paper",
            "&:hover": { borderColor: "primary.main" },
          }}
          elevation={2}
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
        >
          {file ? (
            <Stack spacing={2} alignItems="center">
              <Typography variant="subtitle1" fontWeight="medium">
                {file.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {(file.size / 1024).toFixed(1)} KB
              </Typography>
              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  component="label"
                  startIcon={<CloudUploadIcon />}
                >
                  Change
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileChange}
                  />
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleRemove}
                >
                  Remove
                </Button>
              </Stack>
            </Stack>
          ) : (
            <Stack spacing={2} alignItems="center">
              <CloudUploadIcon sx={{ fontSize: 48, color: "primary.main" }} />
              <Typography variant="subtitle1" fontWeight="medium">
                Drag & Drop your resume here
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Click below to {resumeExists ? "update" : "upload"} (PDF, DOC, DOCX)
              </Typography>
              <Button
                variant="contained"
                component="label"
                startIcon={<CloudUploadIcon />}
              >
                {resumeExists ? "Update Resume" : "Upload Resume"}
                <input
                  type="file"
                  hidden
                  accept=".pdf,.doc,.docx"
                  onChange={handleFileChange}
                />
              </Button>
            </Stack>
          )}
        </Paper>

        {/* Actions */}
        <Divider />
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={handleBack}>
            Back
          </Button>
          <Button
            variant="contained"
            startIcon={<UploadFileIcon />}
            color="primary"
            disabled={!file || isLoading}
            onClick={handleUpload}
          >
            {resumeExists ? "Update" : "Upload"}
          </Button>
        </Stack>
      </Stack>
    </PageContainer>
  );
}
